<template>
  <div class="trade-in-list-table">
    <b-table
      :items="items"
      :fields="fields"
      hover
      bordered
      :busy="isLoading"
      responsive
      show-empty
      empty-text="Không có phiếu thu cũ nào"
      class="w-100"
    >
      <template v-slot:table-busy>
        <vcl-table :speed="5" :animate="true" :columns="6"></vcl-table>
      </template>

      <template v-slot:cell(id)="row">
        <div class="text-center">
          <div
            class="productCode btn-link cursor-pointer"
            @click="viewDetail(row.item)"
          >
            <span v-text="row.item.id"></span>
          </div>

          <div class="mb-1">
            <i
              :class="getTradeInTypeIcon(row.item.tradeInType)"
              class="icon-color text-dark"
              v-b-tooltip.hover
              :title="row.item.tradeInTypeName"
            ></i>
          </div>

          <p class="mb-0 font-weight-bolder">{{ row.item.date }}</p>
          <p class="mb-0">{{ row.item.createdBy }}</p>
        </div>
      </template>

      <template v-slot:cell(customer)="row">
        <p class="mb-0 font-weight-bolder">{{ row.item.customerName }}</p>
        <p class="mb-0">{{ row.item.customerPhone }}</p>
      </template>

      <template v-slot:cell(productName)="row">
        <div class="d-flex align-items-center flex-gap-8">
          <ProductImage
            :src="row.item.productImageUrl"
            class="size-40 object-contain flex-shrink-0"
          />
          <div class="flex-grow-1 min-w-0">
            <p class="m-0 text-medium text-11 lh-1-4">
              {{ row.item.productBarCode }}
            </p>
            <p class="m-0 text-medium text-12 lh-1-4">
              {{ row.item.productCode }}
            </p>
            <p class="m-0 font-weight-bolder lh-1-4">
              {{ row.item.productName }}
            </p>
            <p class="text-imei-no m-0">IMEI: {{ row.item.imeiCode }}</p>
          </div>
        </div>
      </template>

      <template v-slot:cell(estimatePrice)="row">
        <div>
          <span v-text="convertPrice(row.item.estimatePrice)"></span>
        </div>
      </template>

      <template v-slot:cell(finalPrice)="row">
        <div>
          <span
            class="font-weight-bolder"
            v-text="convertPrice(row.item.finalPrice)"
          ></span>
          <div v-if="row.item.differencePrice !== 0" class="mt-1">
            <span
              :class="getDifferenceClass(row.item.differencePrice)"
              class="text-small"
            >
              <i
                :class="getDifferenceIcon(row.item.differencePrice)"
                class="mr-1"
              ></i>
              <span v-text="formatDifference(row.item.differencePrice)"></span>
            </span>
          </div>
        </div>
      </template>

      <template v-slot:cell(statusName)="row">
        <div class="text-center">
          <div class="mb-2">
            <span
              v-if="row.item.status === 1"
              v-text="row.item.statusName"
              class="label font-weight-bolder label-lg label-light-success label-inline note"
            ></span>
            <span
              v-else
              v-text="row.item.statusName"
              class="label font-weight-bolder label-lg label-light-info label-inline note"
            ></span>
          </div>
          <div>
            <b-link
              v-if="row.item.stockSlipCode"
              v-text="row.item.stockSlipCode"
              @click="viewStockSlip(row.item)"
              class="text-sm mt-1 cursor-pointer"
            ></b-link>
          </div>
          <div>
            <span v-if="row.item.billNumber">
              Số HD:
              <span
                class="text-primary cursor-pointer"
                @click="viewBill(row.item.billNumber)"
              >
                {{ row.item.billNumber }}
              </span>
            </span>
          </div>
        </div>
      </template>
    </b-table>

    <b-row>
      <b-col>
        <p class="mt-3 text-dark font-weight-bold">
          Tổng số: {{ convertPrice(totalRow) }}
        </p>
      </b-col>
      <b-col>
        <BPaginationNavCustom
          :number-of-pages="numberOfPage || 1"
          :current-page="currentPage"
          :total-page="numberOfPage"
          :link-params="paginationLinkParams"
          @page-change="$emit('page-change', $event)"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { VclTable } from 'vue-content-loading';
import ProductImage from '@/view/components/products/ProductImage.vue';
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue';
import { convertPrice } from '@/utils/common';
import { TRADE_IN_TYPE } from '@/utils/enum';

export default {
  name: 'TradeInListTable',

  components: {
    VclTable,
    ProductImage,
    BPaginationNavCustom,
  },

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    totalRow: {
      type: Number,
      default: 0,
    },
    numberOfPage: {
      type: Number,
      default: 1,
    },
    currentPage: {
      type: [Number, String],
      default: 1,
    },
    paginationLinkParams: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      fields: [
        { key: 'id', label: 'ID', sortable: false },
        { key: 'storeName', label: 'Cửa hàng', sortable: false },
        { key: 'customer', label: 'Khách hàng', sortable: false },
        { key: 'productName', label: 'Tên sản phẩm', sortable: false },
        { key: 'estimatePrice', label: 'Giá thu dự kiến', sortable: false },
        { key: 'finalPrice', label: 'Giá thu cuối cùng', sortable: false },
        { key: 'note', label: 'Ghi chú', sortable: false },
        { key: 'statusName', label: 'Trạng thái', sortable: false },
      ],
    };
  },

  methods: {
    convertPrice,
    getTradeInTypeIcon(tradeInType) {
      switch (tradeInType) {
        case TRADE_IN_TYPE.TRADE_IN:
          return 'fa fa-retweet';
        case TRADE_IN_TYPE.BUYING_OLD_ITEMS:
          return 'fa fa-cart-arrow-down';
        case TRADE_IN_TYPE.EXCHANGE_WARRANTY:
          return 'fa fa-shield-alt';
        case TRADE_IN_TYPE.TEST_IMEI:
          return 'fa fa-mobile-alt';
        default:
          return 'fa fa-question-circle';
      }
    },
    getDifferenceClass(difference) {
      if (difference > 0) return 'text-success';
      if (difference < 0) return 'text-danger';
      return 'text-muted';
    },
    getDifferenceIcon(difference) {
      if (difference > 0) return 'fas fa-arrow-down';
      if (difference < 0) return 'fas fa-arrow-up';
      return 'fas fa-minus';
    },
    formatDifference(difference) {
      const formatted = convertPrice(Math.abs(difference));
      if (difference === 0) return formatted;
      const sign = difference > 0 ? '-' : '+';
      return `${sign}${formatted}`;
    },
    viewDetail(item) {
      if (!item.id) return;
      const routeData = this.$router.resolve({
        name: 'update-trade-in',
        query: { id: item.id },
      });
      window.open(routeData.href, '_blank');
    },
    viewStockSlip(item) {
      if (!item.stockSlipId) return;
      const routeData = this.$router.resolve({
        name: 'update-import-trade-in',
        query: { id: item.stockSlipId },
      });
      window.open(routeData.href, '_blank');
    },
    viewBill(billNumber) {
      if (!billNumber) return;
      const routeData = this.$router.resolve({
        name: 'update-bill',
        query: { id: billNumber },
      });
      window.open(routeData.href, '_blank');
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
